// Considered: `SphereTimelineEventMolecule`, `HomeMode`, `CompactTimelineMolecule` — these are
// what this organism retires (specs/stream-trajectory-tools.md § Design System Reuse Audit). None
// carries per-aspect tracks, cadence, or a demand stack; `CompactTimelineMolecule` runs its own
// unrelated direct `/timeline` fetch, backward-only. This owns nothing fetch-wise itself — it
// reads `ToolbarContext.trajectoryState`.
//
// Source: design/metaspace-tools/metaspace-trajectory-v2.jsx:123-267 (`TrajectoryPanelV2`). IA:
// header (range switch in the `meta` slot) -> Health track -> Gravity track -> Freshness -> demand
// stack.

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'localization';
import { useAnalytics } from '@center-technologies/analytics';
import type { InspectorAct, InspectorPayload } from 'types/inspector';
import type { TrajectoryBand, TrajectoryRange, TrajectoryTrack } from 'types/trajectory';
import { useToolbarContextOrNull } from '../../../context/ToolbarContext';
import { useApiClientsOrNull } from '../../../context/ApiClientsContext';
import { putContextViewState } from '../../../api/contextApi';
import {
  buildAspectPayload,
  buildCadenceDuePayload,
  buildCadenceHitPayload,
  buildCadenceMissPayload,
  buildCoveragePayload,
  buildFocusItemPayload,
  buildScopePayload,
  buildSparkPointPayload,
  buildStalenessPayload,
  buildTransitionPayload,
} from '../../../helpers/trajectory/buildTrajectoryInspectorPayloads';
import GlyphAtom from '../../atoms/Glyph/GlyphAtom';
import EmptyStateAtom from '../../atoms/EmptyState/EmptyStateAtom';
import SegmentedControlAtom from '../../atoms/SegmentedControl/SegmentedControlAtom';
import StateBandAtom from '../../atoms/StateBand/StateBandAtom';
import VelocitySparklineAtom from '../../atoms/VelocitySparkline/VelocitySparklineAtom';
import ToolInspectorOrganism from '../ToolInspector/ToolInspectorOrganism';
import TrackCardMolecule from '../../molecules/TrackCard/TrackCardMolecule';
import CadenceRowMolecule from '../../molecules/CadenceRow/CadenceRowMolecule';
import CheckInInlineMolecule from '../../molecules/CheckInInline/CheckInInlineMolecule';
import FreshnessCardMolecule from '../../molecules/FreshnessCard/FreshnessCardMolecule';
import DemandStackMolecule from '../../molecules/DemandStack/DemandStackMolecule';
import './TrajectoryPanelOrganism.scss';

export interface TrajectoryScope {
  aspectSystemName: string;
  from: string;
  to: string | null;
}

/** One jump-to-track request (Stream aspect-name click). `nonce` distinguishes repeat jumps to
 * the same aspect so each click re-scrolls/re-flashes. */
export interface TrajectoryFocusRequest {
  aspectSystemName: string;
  nonce: number;
}

export interface TrajectoryPanelOrganismProps {
  sphereId: string;
  /** Scroll to and briefly highlight this aspect's track (Stream → jump-to-track). */
  focusAspect?: TrajectoryFocusRequest | null;
  onOpenFullTimeline?: () => void;
  /** Also fires when a Health band is clicked — the scope-and-inspect gesture the spec ratifies. */
  onScopeStream?: (scope: TrajectoryScope) => void;
  className?: string;
}

const RANGE_OPTIONS: { value: TrajectoryRange; label: string; shortLabel: string }[] = [
  { value: '7d', label: '7d', shortLabel: '7d' },
  { value: '30d', label: '30d', shortLabel: '30d' },
  { value: 'q', label: 'Q', shortLabel: 'Q' },
];

const TrajectoryPanelOrganism: React.FC<TrajectoryPanelOrganismProps> = ({
  sphereId,
  focusAspect,
  onOpenFullTimeline,
  onScopeStream,
  className,
}) => {
  const { t } = useTranslation();
  const logAnalyticsEvent = useAnalytics();
  const apiClients = useApiClientsOrNull();
  const toolbarContext = useToolbarContextOrNull();

  const [inspectorItem, setInspectorItem] = useState<InspectorPayload | null>(null);

  const { tracks, freshness, demands, range, isLoading, error } =
    toolbarContext?.trajectoryState ?? { tracks: [], freshness: null, demands: [], range: '30d' as TrajectoryRange, isLoading: false, error: null };

  const contextRef = `trajectory:${sphereId}`;

  // Jump-to-track focus: applied once per nonce, waiting for the track to exist (the trajectory
  // fetch is lazy, so the target may land a render after the panel mounts).
  const trackRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const appliedFocusNonceRef = useRef<number | null>(null);
  const [focusedTrack, setFocusedTrack] = useState<string | null>(null);
  useEffect(() => {
    if (!focusAspect || appliedFocusNonceRef.current === focusAspect.nonce) return undefined;
    const el = trackRefs.current[focusAspect.aspectSystemName];
    if (!el) return undefined;
    appliedFocusNonceRef.current = focusAspect.nonce;
    el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    setFocusedTrack(focusAspect.aspectSystemName);
    const timer = setTimeout(() => setFocusedTrack(null), 2400);
    return () => clearTimeout(timer);
  }, [focusAspect, tracks]);

  // Choice-state display labels. Unknown enum values fall back to the raw literal (the wireframe's
  // mono style), so arbitrary choice-aspect vocabularies never render a broken key.
  const stateLabel = useCallback(
    (state: string) => t(`trajectory.states.${state}`, { defaultValue: state }),
    [t],
  );

  const handleInspect = useCallback((payload: InspectorPayload) => setInspectorItem(payload), []);
  const handleCloseInspector = useCallback(() => setInspectorItem(null), []);

  const handleCopyLink = useCallback((address: string) => {
    const url = `${globalThis.window.location.origin}${address}`;
    navigator.clipboard?.writeText(url).catch(() => undefined);
    logAnalyticsEvent('chord_link_copied', undefined, { kind: inspectorItem?.kind });
  }, [logAnalyticsEvent, inspectorItem?.kind]);

  const handleRangeChange = useCallback(async (next: TrajectoryRange) => {
    await toolbarContext?.refreshTrajectory(next);
  }, [toolbarContext]);

  const handleBandInspect = useCallback((track: TrajectoryTrack) => (payload: InspectorPayload, band: TrajectoryBand) => {
    setInspectorItem(payload);
    onScopeStream?.({ aspectSystemName: track.aspectSystemName, from: band.from, to: band.to });
  }, [onScopeStream]);

  const handleTransitionInspect = useCallback((payload: InspectorPayload) => setInspectorItem(payload), []);

  const handlePinToggle = useCallback(async (track: TrajectoryTrack) => {
    if (!apiClients?.schemaClient) return;
    const nextPinned = !track.pinned;
    const pins = tracks.filter((t2) => (t2.aspectSystemName === track.aspectSystemName ? nextPinned : t2.pinned)).map((t2) => t2.aspectSystemName);
    await putContextViewState(apiClients.schemaClient, contextRef, { viewConfig: { pins } });
    logAnalyticsEvent('trajectory_track_pinned', undefined, { aspectSystemName: track.aspectSystemName, pinned: nextPinned });
    await toolbarContext?.refreshTrajectory();
  }, [apiClients?.schemaClient, tracks, contextRef, logAnalyticsEvent, toolbarContext]);

  const handleAct = useCallback(async (act: InspectorAct, item: InspectorPayload): Promise<void> => {
    if (!apiClients?.graphClient) throw new Error('API clients not available');

    switch (act.id) {
      case 'copy_link':
        handleCopyLink(item.address);
        return;
      case 'scope_stream':
        return;
      case 'request_update':
      case 'remind_owner': {
        await apiClients.graphClient.post(`/spheres/${sphereId}/demands`, {
          aspectSystemName: item.context.aspectSystemName,
          kind: act.id === 'request_update' ? 'revalidate' : 'remind',
          periodId: item.context.periodId,
        });
        logAnalyticsEvent('demand_requested', undefined, {
          kind: act.id === 'request_update' ? 'revalidate' : 'remind',
          aspectSystemName: item.context.aspectSystemName,
        });
        await toolbarContext?.refreshTrajectory();
        return;
      }
      case 'check_in_late':
      case 'fulfil_inline':
      case 'set_status_on_track':
      case 'set_status_at_risk':
      case 'set_status_blocked': {
        const statusValue = act.id.startsWith('set_status_') ? act.id.replace('set_status_', '') : undefined;
        if (!statusValue || !item.context.factorId) return;
        await apiClients.graphClient.post(`/sphere/${sphereId}/factors/${item.context.factorId}/checkin`, {
          statusValue,
          periodId: item.context.periodId,
        });
        logAnalyticsEvent('trajectory_checkin_submitted', undefined, { statusValue, late: act.id === 'check_in_late', source: 'trajectory' });
        await toolbarContext?.refreshTrajectory();
        return;
      }
      case 'open_full_track':
        onOpenFullTimeline?.();
        return;
      case 'pin_to_glimpse': {
        const track = tracks.find((t2) => t2.aspectSystemName === item.context.aspectSystemName);
        if (track) await handlePinToggle(track);
        return;
      }
      default:
        return;
    }
  }, [apiClients?.graphClient, handleCopyLink, sphereId, logAnalyticsEvent, toolbarContext, onOpenFullTimeline, tracks, handlePinToggle]);

  const handleCheckInSubmitted = useCallback(async (track: TrajectoryTrack, statusValue: string) => {
    logAnalyticsEvent('trajectory_checkin_submitted', undefined, { statusValue, late: false, source: 'trajectory' });
    await toolbarContext?.refreshTrajectory();
  }, [logAnalyticsEvent, toolbarContext]);

  const coverage = useMemo(
    () => tracks
      .filter((track) => track.freshness.fresh !== null)
      .map((track) => ({ aspectSystemName: track.aspectSystemName, label: track.label, fresh: Boolean(track.freshness.fresh), ageDays: track.freshness.ageDays })),
    [tracks]
  );

  return (
    <div className={['trajectory-panel-organism', className].filter(Boolean).join(' ')}>
      <div className="trajectory-panel-organism__header">
        <span className="trajectory-panel-organism__title">
          <GlyphAtom glyph="lucide:trending-up" />
          {t('trajectory.panel.title')}
        </span>
        <span className="trajectory-panel-organism__meta" title={t('trajectory.panel.rangePeek', { defaultValue: 'Range persists per View' })}>
          <SegmentedControlAtom<TrajectoryRange>
            options={RANGE_OPTIONS}
            value={range}
            onChange={(value) => handleRangeChange(value as TrajectoryRange)}
            mode="single"
            variant="pills"
            size="small"
            useShortLabels
            ariaLabel={t('trajectory.panel.rangePeek', { defaultValue: 'Range' })}
          />
        </span>
        <button type="button" className="trajectory-panel-organism__full-timeline" onClick={onOpenFullTimeline}>
          {t('trajectory.panel.fullTimeline', { defaultValue: 'Full timeline' })} <GlyphAtom glyph="lucide:arrow-up-right" />
        </button>
      </div>

      {inspectorItem ? (
        <ToolInspectorOrganism
          item={inspectorItem}
          backLabel={t('trajectory.panel.title')}
          onClose={handleCloseInspector}
          onAct={handleAct}
          onCopyLink={handleCopyLink}
        />
      ) : (
        <div className="trajectory-panel-organism__body">
          {tracks.length === 0 && !isLoading && (
            <EmptyStateAtom icon="pi-chart-line" title={t('trajectory.empty.title', { defaultValue: 'Nothing to trend yet' })} description={t('trajectory.empty.description', { defaultValue: 'Tracks appear here once this sphere has observations.' })} />
          )}

          {error && <div className="trajectory-panel-organism__error">{error}</div>}

          {tracks.map((track) => {
            const cadenceLabel = track.cadence
              ? t('trajectory.cadence.rotation', { defaultValue: "weekly rotation · this aspect's own tempo" })
              : undefined;
            const dueEntry = track.cadence?.find((entry) => entry.state === 'due');

            return (
              <div
                key={track.aspectSystemName}
                ref={(el) => { trackRefs.current[track.aspectSystemName] = el; }}
                className={[
                  'trajectory-panel-organism__track',
                  focusedTrack === track.aspectSystemName && 'trajectory-panel-organism__track--focused',
                ].filter(Boolean).join(' ')}
              >
              <TrackCardMolecule
                title={track.label}
                value={track.current.label ? `${track.current.value} · ${track.current.label}` : stateLabel(String(track.current.value))}
                titlePayload={buildAspectPayload(track, sphereId, t)}
                onInspect={handleInspect}
                pinned={track.pinned}
                onPin={() => handlePinToggle(track)}
                footer={track.cadence && (
                  <>
                    <span className="trajectory-panel-organism__cadence-caption">{cadenceLabel}</span>
                    {dueEntry && (
                      <span className="trajectory-panel-organism__checkin-slot">
                        <CheckInInlineMolecule
                          sphereId={sphereId}
                          factorId={dueEntry.factorId}
                          onSubmitted={(statusValue) => handleCheckInSubmitted(track, statusValue)}
                        />
                      </span>
                    )}
                  </>
                )}
              >
                {track.kind === 'choice' && track.bands && (
                  <StateBandAtom
                    bands={track.bands}
                    stateLabel={stateLabel}
                    buildBandPayload={(band) => buildScopePayload(track, band, sphereId, t)}
                    buildTransitionPayload={(band) => buildTransitionPayload(track, band, sphereId, t)}
                    onBandInspect={handleBandInspect(track)}
                    onTransitionInspect={handleTransitionInspect}
                  />
                )}
                {track.kind === 'numeric' && track.series && (
                  <VelocitySparklineAtom
                    dataPoints={track.series.map((point) => ({ score: point.value, timestamp: point.t }))}
                    domain={[Math.min(...track.series.map((p) => p.value)), Math.max(...track.series.map((p) => p.value))]}
                    endpointDot
                    onEndpointClick={() => {
                      const lastPoint = track.series?.[track.series.length - 1];
                      if (lastPoint) handleInspect(buildSparkPointPayload(track, lastPoint, sphereId, t));
                    }}
                  />
                )}
                {track.cadence && track.cadence.length > 0 && (
                  <CadenceRowMolecule
                    entries={track.cadence}
                    buildPayload={(entry) => {
                      if (entry.state === 'hit') return buildCadenceHitPayload(track, entry, sphereId, t);
                      if (entry.state === 'miss') return buildCadenceMissPayload(track, entry, sphereId, t);
                      return buildCadenceDuePayload(track, entry, sphereId, t);
                    }}
                    onInspect={handleInspect}
                  />
                )}
              </TrackCardMolecule>
              </div>
            );
          })}

          {freshness && (
            <TrackCardMolecule
              title={t('trajectory.track.freshnessTitle', { defaultValue: 'Freshness' })}
              value={t('trajectory.freshness.summary', { fresh: freshness.fresh, total: freshness.total, defaultValue: `${freshness.fresh} of ${freshness.total} aspects fresh` })}
              titlePayload={buildCoveragePayload(freshness, tracks, sphereId, t)}
              onInspect={handleInspect}
            >
              <FreshnessCardMolecule
                freshness={freshness}
                coverage={coverage}
                buildBlockPayload={(block) => buildAspectPayload(tracks.find((tr) => tr.aspectSystemName === block.aspectSystemName) as TrajectoryTrack, sphereId, t)}
                buildStalenessPayload={(deviant) => buildStalenessPayload(deviant, sphereId, t)}
                onInspect={handleInspect}
              />
            </TrackCardMolecule>
          )}

          {demands.length > 0 && (
            <DemandStackMolecule
              sphereId={sphereId}
              demands={demands}
              buildPayload={(demand) => buildFocusItemPayload(demand, sphereId, t)}
              onInspect={handleInspect}
              onCheckInSubmitted={() => toolbarContext?.refreshTrajectory()}
            />
          )}
        </div>
      )}
    </div>
  );
};

TrajectoryPanelOrganism.displayName = 'TrajectoryPanelOrganism';

export default TrajectoryPanelOrganism;
